import type { IAppContext } from 'App/AppContext';
import type { TFunction } from 'i18next';
import { DBProcesLoggerLogLevel, LogDTO } from 'models/dto/logDTO';
import { apiAddProcessLog } from 'services/Api/userService';
import { apiRequest } from 'services/Auth/authConfig';
import Config from 'services/Config/configService';
import logger, { getLogWithDateStamp, globalLog } from 'services/Logging/logService';
import AppError, { normalizeError } from 'utils/appError';
import { safeStringify } from 'utils/stringify';
import { isAbortOrCancelError, isAuthReloadError, isLoginError, isNetworkError } from './ErrorMessageHelper';

export const getErrorMessageText = (error: AppError, t: TFunction<string[]>): string => {
  if (isNetworkError(error) || isAbortOrCancelError(error) || isLoginError(error)) {
    return t('translation:Errors.NetworkError.Message');
  }

  switch (error.code) {
    case '429':
      return t('translation:Errors.TooManyRequests.Message');
    case '404':
      return t('translation:Errors.DatabaseNotFound.Message');
    case '409':
      return t('translation:Errors.DatabaseConflict.Message');
    case '401':
    case '403':
      return t('translation:Errors.AccessDenied.Message');
    case '400':
    case '500':
      if (error.debug && error.debug.indexOf('AccessDenied') >= 0) {
        return t('translation:Errors.AccessDenied.Message');
      }

      if (AppError.isControllerException(error)) {
        return AppError.getControllerExceptionMsg(error, t) ?? error.message;
      }

      return t('translation:Errors.ApiError.Message');
    case '999':
      return 'An unknown fatal error has occurred. We are sorry for the inconvenience! Please reload your browser and contact our Support Desk (support@isoplanner.app) when the problem persists.';
    default:
      return error.message;
  }
};

export const saveErrorToApi = async (
  error: AppError | undefined,
  appContext: IAppContext,
  t: TFunction<string[]>,
): Promise<boolean> => {
  try {
    if (!error) {
      return false;
    }

    // A connectivity failure means the API is unreachable by definition, so uploading
    // the log is guaranteed to fail and only produces more noise (a failed POST plus a
    // cascading upload error). Skip the attempt entirely.
    if (isNetworkError(error)) {
      return false;
    }

    const logDTO = new LogDTO(
      error.fromApi === true
        ? `Global client log for API exception ${error.apiLogId || 0}`
        : error.message ?? 'Unknown error',
    );

    logDTO.error = safeStringify(error);
    logDTO.add(getLogWithDateStamp('Environment info: ' + Config.getEnvInfo()));
    logDTO.add(getLogWithDateStamp(error.stack));
    logDTO.add(getLogWithDateStamp(error.debug));
    logDTO.add('Global log entries');
    globalLog.messages.forEach((message) => logDTO.add(message));

    const filterMsg: string[] = [
      'access_denied',
      'interaction_in_progress',
      'user_cancelled',
      'consent_required',
      'popup_window_error',
      'hash_empty_error',
      'Authentication_MSGraphPermissionMissing',
      'Authorization_RequestDenied',
      'InvalidAuthenticationToken',
      'Authorization_IdentityNotFound',
      'NoPermissionsInAccessToken',
      'AADSTS7000229',
      'AADSTS500011',
      'AADSTS50173',
      'AADSTS650051'
    ];

    const filterBackEndStatusCodes: string[] = ['403', '404', '409'];
    const isRateLimited = error.code === '429';

    let shouldNotCreateTicket =
      !isRateLimited &&
      (error.fromApi === true ||
        isNetworkError(error) ||
        isAuthReloadError(error) ||
        isAbortOrCancelError(error) ||
        (error.code ? filterBackEndStatusCodes.includes(error.code) : false));

    if (shouldNotCreateTicket === false) {
      shouldNotCreateTicket = filterMsg.some((message) => {
        if (error.code === message) {
          return true;
        }

        if (error.message && error.message.toLowerCase().indexOf(message) >= 0) {
          return true;
        }

        if (error.debug && error.debug.toLowerCase().indexOf(message) >= 0) {
          return true;
        }

        return false;
      });
    }

    if (isRateLimited) {
      logDTO.name = 'Global client log for API exception - Rate limit exceeded';
      logDTO.level = DBProcesLoggerLogLevel.Warning;
    } else if (shouldNotCreateTicket) {
      logDTO.level = DBProcesLoggerLogLevel.Info;
    } else {
      logDTO.level = DBProcesLoggerLogLevel.Error;
    }

    const accessToken = await appContext.getAccessToken(apiRequest.scopes);
    await apiAddProcessLog(logDTO, accessToken);
    globalLog.clear();

    return true;
  } catch (err) {
    // If the upload itself failed because the API is unreachable (backend down or the
    // client is offline), there is nothing the user can do about it and re-surfacing it
    // only piles a scary notification on top of the original error. Log quietly and stop.
    if (isNetworkError(normalizeError(err))) {
      logger.debug('Skipped uploading client log to API: network unavailable');

      return false;
    }

    logger.error(new AppError('Error while uploading log to API. ' + safeStringify(err)));
    appContext.showNotification(t('translation:Errors.UploadClientLog.Message'), true);

    return false;
  }
};
